import { useApolloClient } from '@apollo/client/react';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useLocale } from 'src/routes/localeContext';
import { useNullableSessionUser } from 'src/routes/sessionContext';
import { createPageTranslations } from './pageTranslations';
import {
    ReaderText_GetTranslationsDocument,
    ReaderText_RequestTranslationsDocument,
} from './ReaderText.generated';

const context = createContext<ReturnType<typeof createPageTranslations> | null>(null);

export const PageTranslationProvider = ({
    children,
    pageKey,
}: {
    children: ReactNode;
    pageKey?: string;
}) => {
    const client = useApolloClient();
    const user = useNullableSessionUser();
    const locale = useLocale();
    const { pathname } = useLocation();
    const scope = pageKey ?? pathname;
    // Query parameters (including pagination) stay within the same page visit.
    // Memoization owns the coordinator's timer and subscriptions across renders.
    const coordinator = useMemo(
        () =>
            createPageTranslations(
                async (requests, signal) => {
                    const response = await client.query({
                        query: ReaderText_GetTranslationsDocument,
                        variables: { requests },
                        fetchPolicy: 'no-cache',
                        context: { suppressGlobalErrorToast: true, fetchOptions: { signal } },
                    });
                    return response.data?.textTranslations ?? [];
                },
                async (requests, signal) => {
                    const response = await client.mutate({
                        mutation: ReaderText_RequestTranslationsDocument,
                        variables: { input: { requests } },
                        context: { suppressGlobalErrorToast: true, fetchOptions: { signal } },
                    });
                    return response.data?.requestTextTranslations?.translations ?? [];
                },
            ),
        [client, user?.id, locale, scope],
    );
    useEffect(() => {
        coordinator.activate();
        return () => coordinator.dispose();
    }, [coordinator]);
    return <context.Provider value={coordinator}>{children}</context.Provider>;
};

export const usePageTranslations = () => {
    const coordinator = useContext(context);
    if (!coordinator) throw new Error('PageTranslationProvider is not detected.');
    return coordinator;
};
